<template>
   <div class="relative bg-overlay before:bg-title before:bg-opacity-95" :style="{backgroundImage:'url('+ bg +')'}">
        <div class="s-pt-100">
            <div class="container-fluid">
                <div class="max-w-lg lg:max-w-[1052px] mx-auto flex items-end justify-between gap-3 lg:gap-8 flex-col lg:flex-row s-pb-100">
                    <div class="md:max-w-[473px] w-full">
                        <h2 class="font-bold leading-none text-white">Newsletter</h2>
                        <p class="mt-2 sm:mt-4 text-white-light">Stay in the loop with exclusive offers and updates. Subscribe to our newsletter for the latest trends and promotions delivered straight to your inbox. </p>
                    </div>
                    <form class="mt-4 lg:mt-6 md:max-w-[478px] w-full" @submit.prevent="submit">
                        <div class="sm:flex">
                            <input v-model="email" class="w-full h-12 md:h-14 bg-white bg-opacity-[0.03] border border-white text-white placeholder:text-white-light border-opacity-30 focus:border-primary p-4 outline-none sm:flex-1 sm:border-r-0 duration-300" type="email" placeholder="Enter your email address">
                            <button class="w-full h-12 bg-primary text-white flex items-center justify-center text-base md:text-lg font-medium p-3 mt-3 sm:mt-0 sm:w-32 sm:h-auto sm:flex-none" :disabled="loading">
                                {{ loading ? 'Saving...' : 'Subscribe' }}
                            </button>
                        </div>
                        <p v-if="successMessage" class="mt-3 text-sm text-primary">{{ successMessage }}</p>
                        <p v-else-if="errorMessage" class="mt-3 text-sm text-red-300">{{ errorMessage }}</p>
                    </form>
                </div>

                <div class="max-w-[1722px] mx-auto flex items-start justify-between gap-10 s-pb-100 flex-wrap lg:flex-nowrap footer-wrapper">

                    <div class="lg:max-w-[366px] w-full sm:w-[45%] lg:w-full flex items-start justify-between gap-6 sm:gap-10 footer-inner-wrapper">
                        <div>
                            <h4 class="text-white leading-none mb-5 md:mb-6 text-xl md:text-2xl">Sitemap</h4>
                            <ul class="text-white leading-none flex flex-col gap-4">
                                <li v-for="(item, index) in footerLink1" :key="index" class="duration-100 hover:text-primary inline-block group"><router-link class="text-underline-primary" :to="item.link">{{item.name}}</router-link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 class="text-white leading-none mb-5 md:mb-6 text-xl md:text-2xl">Others</h4>
                            <ul class="text-white leading-none flex flex-col gap-4">
                                <li v-for="(item, index) in footerLink2" :key="index" class="duration-100 hover:text-primary inline-block group"><router-link class="text-underline-primary" :to="item.link">{{item.name}}</router-link></li>
                            </ul>
                        </div>
                    </div>

                    <div class="lg:max-w-[522px] w-full sm:text-center -order-1 lg:order-none">
                        <div class="sm:mx-auto">
                            <span class="block text-white font-semibold text-[22px] sm:text-[26px] leading-none tracking-tight">{{ company.name }}</span>
                            <span class="mt-2 block text-[11px] uppercase tracking-[0.22em] text-primary">{{ company.shortTagline }}</span>
                        </div>
                        <p class="mt-4 text-white-light max-w-[522px] sm:mx-auto">{{ company.description }}</p>
                        <div class="flex items-center sm:justify-center gap-4 mt-6 flex-wrap">
                            <a
                                v-for="link in socialLinks"
                                :key="link.label"
                                :href="link.href"
                                target="_blank"
                                rel="noopener"
                                class="w-10 h-10 rounded-full border border-white border-opacity-50 flex items-center justify-center text-white hover:border-primary hover:text-primary duration-300"
                                :aria-label="link.label"
                                :title="link.label"
                            >
                                <i :class="link.icon"></i>
                            </a>
                        </div>
                    </div>

                    <div class="lg:max-w-[460px] w-full sm:w-[45%] lg:w-full flex items-start justify-between gap-6 sm:gap-10 footer-inner-wrapper">
                        <div>
                            <h4 class="text-white leading-none mb-5 md:mb-6 text-xl md:text-2xl">Shop</h4>
                            <ul class="text-white leading-none flex flex-col gap-4">
                                <li v-for="(item, index) in footerLink3" :key="index" class="duration-100 hover:text-primary inline-block group"><router-link class="text-underline-primary" :to="item.link">{{item.name}}</router-link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 class="text-white leading-none mb-5 md:mb-6 text-xl md:text-2xl">Customer Service</h4>
                            <ul class="text-white leading-none flex flex-col gap-4">
                                <li v-for="(item, index) in footerLink4" :key="index" class="duration-100 hover:text-primary inline-block group"><router-link class="text-underline-primary" :to="item.link">{{item.name}}</router-link></li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div class="max-w-[1722px] mx-auto border-t border-white border-opacity-10 py-5 md:py-7 text-center">
                    <p class="text-white">© {{ year }} {{ company.name }} — {{ company.address }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import bg from '@/assets/img/bg/footer.jpg'
    import { footerLink1, footerLink2, footerLink3, footerLink4 } from '@/data/nav-data';
    import { computed, ref } from 'vue';
    import { useNewsletterForm } from '@/composables/use-newsletter';
    import { company, socialProfiles } from '@/data/company';

    // Only the channels we actually have. Icons pointing at "#" are worse than no
    // icons: they read as a broken site.
    const socialLinks = computed(() => [
        ...socialProfiles.map((link) => ({ ...link, icon: `${link.icon} text-[18px]` })),
        { label: `Call ${company.phone}`, href: company.phoneHref, icon: 'fa-solid fa-phone text-[15px]' },
        { label: 'Email us', href: `mailto:${company.email}`, icon: 'fa-solid fa-envelope text-[15px]' },
    ]);

    const year = ref(new Date().getFullYear());
    const { email, loading, successMessage, errorMessage, submit } = useNewsletterForm();
</script>

<style>
/* Keep each pair of link columns side by side on small phones (the template stacks them). */
@media screen and (max-width: 390px) {
    .footer-wrapper .footer-inner-wrapper { flex-direction: row !important; }
    .footer-wrapper .footer-inner-wrapper > div { flex: 1 1 0; min-width: 0; }
    .footer-wrapper .footer-inner-wrapper h4 { font-size: 18px; }
}
</style>
